import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PropertyMapDrawing from '../components/PropertyMapDrawing';
import '../styles/ContactPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    adresse: '',
    courriel: '',
    sourceReference: '',
    services: [],
    notes: '',
    photos: [],
    propertyArea: 0,
    propertyCoordinates: []
  });

  const [formStatus, setFormStatus] = useState({ submitted: false, message: '', isError: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [propertyData, setPropertyData] = useState(null);
  const [addressLocation, setAddressLocation] = useState(null);

  const servicesOptions = [
    'Fertilisation naturelle',
    'Fertilisation et contrôle de mauvaises herbes',
    'Taille de haie',
    'Surensemencement'
  ];

  const sourceOptions = [
    'Recherche Google',
    'Recommandation d\'un ami',
    'Facebook / Instagram',
    'Panneau publicitaire',
    'Autre'
  ];

  const handleAreaCalculated = (data) => {
    setPropertyData(data);
    if (data) {
      setFormData(prev => ({
        ...prev,
        propertyArea: data.area,
        propertyCoordinates: data.coordinates
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        propertyArea: 0,
        propertyCoordinates: []
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 5 photos max
    if (files.length > 5) {
      setFormStatus({
        submitted: true,
        message: 'Maximum 5 photos autorisées.',
        isError: true
      });
      setTimeout(() => {
        setFormStatus({ submitted: false, message: '', isError: false });
      }, 5000);
      return;
    }
    
    // Check file sizes (max 5MB per file)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setFormStatus({
        submitted: true,
        message: 'Chaque photo doit faire moins de 5 MB.',
        isError: true
      });
      setTimeout(() => {
        setFormStatus({ submitted: false, message: '', isError: false });
      }, 5000);
      return;
    }
    
    setUploadedFiles(files);
    setFormData(prev => ({
      ...prev,
      photos: files
    }));
  };

  const removeFile = (index) => {
    const newFiles = Array.from(uploadedFiles).filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    setFormData(prev => ({
      ...prev,
      photos: newFiles
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        services: checked 
          ? [...prev.services, value]
          : prev.services.filter(s => s !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one service is selected
    if (formData.services.length === 0) {
      setFormStatus({
        submitted: true,
        message: 'Veuillez sélectionner au moins un service.',
        isError: true
      });
      setTimeout(() => {
        setFormStatus({ submitted: false, message: '', isError: false });
      }, 5000);
      return;
    }
    
    // Validate property area is drawn
    if (!formData.propertyArea || formData.propertyArea === 0) {
      setFormStatus({
        submitted: true,
        message: 'Veuillez dessiner le contour de votre terrain sur la carte.',
        isError: true
      });
      setTimeout(() => {
        setFormStatus({ submitted: false, message: '', isError: false });
      }, 5000);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('prenom', formData.prenom);
      formDataToSend.append('nom', formData.nom);
      formDataToSend.append('adresse', formData.adresse);
      formDataToSend.append('courriel', formData.courriel);
      formDataToSend.append('sourceReference', formData.sourceReference);
      formDataToSend.append('services', JSON.stringify(formData.services));
      formDataToSend.append('notes', formData.notes || '');
      formDataToSend.append('propertyArea', formData.propertyArea);
      formDataToSend.append('propertyCoordinates', JSON.stringify(formData.propertyCoordinates));
      
      // Append photos
      uploadedFiles.forEach((file, index) => {
        formDataToSend.append('photos', file);
      });
      
      // Submit to backend API
      const response = await axios.post(`${API_URL}/api/submit-form`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setFormStatus({
          submitted: true,
          message: response.data.message,
          isError: false
        });
        
        // Reset form
        setFormData({
          prenom: '',
          nom: '',
          adresse: '',
          courriel: '',
          sourceReference: '',
          services: [],
          notes: '',
          photos: [],
          propertyArea: 0,
          propertyCoordinates: []
        });
        setUploadedFiles([]);
        setPropertyData(null);
        setAddressLocation(null);
        
        // Reset status after 8 seconds
        setTimeout(() => {
          setFormStatus({ submitted: false, message: '', isError: false });
        }, 8000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus({
        submitted: true,
        message: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.',
        isError: true
      });
      
      setTimeout(() => {
        setFormStatus({ submitted: false, message: '', isError: false });
      }, 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-new">
      {/* Hero Section */}
      <section className="contact-hero-new">
        <div className="container">
          <p className="contact-subtitle">CONTACTEZ-NOUS</p>
          <h1 className="contact-title">Demande de soumission</h1>
          <div className="title-underline"></div>
          <p className="contact-description">
            Remplissez le formulaire ci-dessous pour obtenir une soumission gratuite.<br />
            Nous vous répondrons dans les 24 heures.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-content-new">
        <div className="container">
          <div className="contact-grid-new">
            {/* Contact Info Cards - Left Side */}
            <div className="contact-info-cards">
              <div className="info-card">
                <div className="info-icon">
                  <Phone size={24} />
                </div>
                <div className="info-content">
                  <p className="info-label">TÉLÉPHONE</p>
                  <a href="tel:4502101858" className="info-value">450 210 1858</a>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">
                  <Mail size={24} />
                </div>
                <div className="info-content">
                  <p className="info-label">COURRIEL</p>
                  <a href="mailto:info@nvfertilisation.com" className="info-value">info@nvfertilisation.com</a>
                </div>
              </div>

              <div className="info-card">
                <div className="info-icon">
                  <MapPin size={24} />
                </div>
                <div className="info-content">
                  <p className="info-label">RÉGION DESSERVIE</p>
                  <p className="info-value">Estrie</p>
                </div>
              </div>

              <div className="hours-card-new">
                <h3 className="hours-title">Heures d'ouverture</h3>
                <div className="hours-list-new">
                  <div className="hours-row">
                    <span>Lundi - Vendredi</span>
                    <span className="hours-time">8h00 - 16h00</span>
                  </div>
                  <div className="hours-row">
                    <span>Samedi - Dimanche</span>
                    <span className="hours-time">Fermé</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form - Right Side */}
            <div className="form-card-new">
              {formStatus.submitted && (
                <div className={formStatus.isError ? "error-alert" : "success-alert"}>
                  {formStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form-new">
                {/* Nom et Prénom */}
                <div className="form-row-new">
                  <div className="form-field">
                    <label>Prénom *</label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      placeholder="Votre prénom"
                      required
                    />
                  </div>
                  
                  <div className="form-field">
                    <label>Nom *</label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                </div>

                {/* Téléphone et Courriel */}
                <div className="form-row-new">
                  <div className="form-field">
                    <label>Téléphone *</label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      placeholder="(450) 123-4567"
                      required
                    />
                  </div>
                  
                  <div className="form-field">
                    <label>Courriel *</label>
                    <input
                      type="email"
                      name="courriel"
                      value={formData.courriel}
                      onChange={handleChange}
                      placeholder="votre@courriel.com"
                      required
                    />
                  </div>
                </div>

                {/* Adresse */}
                <div className="form-field">
                  <label>Adresse complète *</label>
                  <AddressAutocomplete
                    value={formData.adresse}
                    onChange={handleChange}
                    onPlaceSelected={(place) => {
                      // Extract coordinates from the selected place
                      if (place.geometry && place.geometry.location) {
                        const location = {
                          lat: place.geometry.location.lat(),
                          lng: place.geometry.location.lng()
                        };
                        setAddressLocation(location);
                        console.log('Adresse sélectionnée avec coordonnées:', location);
                      }
                    }}
                  />
                  <span className="field-hint">Commencez à taper votre adresse - l'autocomplete vous aidera</span>
                </div>

                {/* Source de référence */}
                <div className="form-field">
                  <label>Comment avez-vous entendu parler de nous? *</label>
                  <select
                    name="sourceReference"
                    value={formData.sourceReference}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez une option</option>
                    {sourceOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Services souhaités */}
                <div className="form-field">
                  <label>Service(s) souhaité(s) *</label>
                  <div className="checkbox-group">
                    {servicesOptions.map((service) => (
                      <label key={service} className="checkbox-label">
                        <input
                          type="checkbox"
                          name="services"
                          value={service}
                          checked={formData.services.includes(service)}
                          onChange={handleChange}
                        />
                        <span className="checkbox-text">{service}</span>
                      </label>
                    ))}
                  </div>
                  <span className="field-hint">Sélectionnez un ou plusieurs services</span>
                </div>

                {/* Notes additionnelles - Optionnel */}
                <div className="form-field">
                  <label>Notes additionnelles (optionnel)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Décrivez votre projet, mentionnez des détails spécifiques, dimensions du terrain, etc."
                    rows="4"
                    className="form-textarea"
                  />
                  <span className="field-hint">Toute information supplémentaire qui pourrait nous aider</span>
                </div>

                {/* Photos - Optionnel */}
                <div className="form-field">
                  <label>Photos de votre terrain (optionnel)</label>
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="photos"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      className="file-input"
                    />
                    <label htmlFor="photos" className="file-upload-label">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Choisir des photos
                    </label>
                  </div>
                  
                  {uploadedFiles.length > 0 && (
                    <div className="uploaded-files">
                      {uploadedFiles.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <span className="file-name">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="remove-file-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <span className="field-hint">Maximum 5 photos, 5 MB chacune (JPG, PNG)</span>
                </div>

                {/* Property Map Drawing - Required */}
                <div className="form-field">
                  <label>Dessinez votre terrain *</label>
                  <PropertyMapDrawing 
                    onAreaCalculated={handleAreaCalculated}
                    addressLocation={addressLocation}
                  />
                  <span className="field-hint">
                    {propertyData 
                      ? `✅ Terrain dessiné: ${propertyData.area.toLocaleString()} pi²` 
                      : '⚠️ Dessinez le contour de votre terrain pour continuer'}
                  </span>
                </div>

                <button type="submit" className="submit-btn-new" disabled={isSubmitting}>
                  <Send size={20} />
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
